import { AccessTime, CallSplit, MailOutline, ScheduleSendOutlined, SwapCalls } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';
import { Fragment, ReactNode } from 'react';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import FormLayout from 'domains/core/components/FormLayout';

import {
    disabledButtonStyles,
    iconStyles,
    labelStyles,
    sharedButtonStyles,
} from './CustomCampaignChooseNextStep/CustomCampaignChooseNextStep.styles';
import DisabledTooltipContent, { DisabledTooltip } from '../../components/DisabledTooltipContent';

export const MAX_SENDS_AMOUNT = 12;

export const getSteps = (
    getIsStepDisabled: (stepName: CustomCampaignSteps) => boolean,
    maxSendsReached: boolean,
    isEditingPausedCampaign: boolean = false,
    conditionalSplitDisabledMessage: ReactNode = 'Add a time delay before your split.'
) => [
    {
        disabled: getIsStepDisabled(CustomCampaignSteps.SELECT_CONTENT),
        disabledMessage: isEditingPausedCampaign
            ? "This can't be added."
            : maxSendsReached
            ? `You've reached the maximum number of sends.`
            : 'Add a time delay before your next send.',
        icon: <MailOutline sx={iconStyles} />,
        label: 'email',
        value: CustomCampaignSteps.SELECT_CONTENT,
    },
    {
        disabled: getIsStepDisabled(CustomCampaignSteps.SET_TIME_DELAY),
        disabledMessage: isEditingPausedCampaign ? (
            <DisabledTooltip />
        ) : maxSendsReached ? (
            "You've reached the maximum number of time delays."
        ) : (
            'Choose an email or split before your next time delay.'
        ),
        icon: <AccessTime sx={iconStyles} />,
        label: 'time delay',
        value: CustomCampaignSteps.SET_TIME_DELAY,
    },
    {
        disabled: getIsStepDisabled(CustomCampaignSteps.CONDITIONAL_SPLIT),
        disabledMessage: isEditingPausedCampaign ? (
            <DisabledTooltip />
        ) : maxSendsReached ? (
            `You've reached the maximum number of splits.`
        ) : (
            conditionalSplitDisabledMessage
        ),
        icon: <CallSplit sx={iconStyles} />,
        label: 'conditional split',
        value: CustomCampaignSteps.CONDITIONAL_SPLIT,
    },
    {
        disabled: getIsStepDisabled(CustomCampaignSteps.SCHEDULE_CURATION),
        disabledMessage: 'Choose an action before you exit.',
        icon: <ScheduleSendOutlined sx={iconStyles} />,
        label: 'schedule & publish curation',
        value: CustomCampaignSteps.SCHEDULE_CURATION,
    },
];

export type step = {
    disabled?: boolean;
    disabledMessage?: ReactNode;
    icon: ReactNode;
    label: string;
    value: CustomCampaignSteps;
};

export const renderChooseStepsForm = (
    handleStepClick: (event: React.ChangeEvent<HTMLInputElement>) => void,
    selectedStep: string,
    steps: step[]
) => (
    <FormLayout header="choose next step" stepName="actions" icon={<SwapCalls />}>
        <RadioGroup onChange={handleStepClick} value={selectedStep}>
            {steps.map((step: step) => {
                const label = (
                    <FormControlLabel
                        control={<Radio sx={{ display: 'none' }} />}
                        label={
                            <span style={labelStyles}>
                                {step.icon}
                                {step.label}
                            </span>
                        }
                        sx={{
                            ...sharedButtonStyles,
                            ...(step.disabled && disabledButtonStyles),
                        }}
                        value={step.value}
                    />
                );
                const useDisabledTooltip =
                    step.disabled && typeof step.disabledMessage !== 'string' && step.disabledMessage !== undefined;
                return (
                    <Fragment key={step.value}>
                        {useDisabledTooltip ? (
                            <DisabledTooltipContent disabled>{label}</DisabledTooltipContent>
                        ) : (
                            <Tooltip title={step.disabled ? step.disabledMessage : ''} arrow followCursor>
                                {label}
                            </Tooltip>
                        )}
                    </Fragment>
                );
            })}
        </RadioGroup>
    </FormLayout>
);

// Email content validation utilities

/**
 * Helper function to validate if an email send has complete content
 * @param emailSend - The email send object to validate
 * @returns boolean indicating if the email has complete content
 */
export const hasCompleteEmailContent = (emailSend: any) =>
    !!emailSend.templateId &&
    !!emailSend.senderProfileId &&
    !!emailSend.subjectLine &&
    emailSend.subjectLine.length >= 3 &&
    (!emailSend.preHeader || !emailSend.preHeader.length || emailSend.preHeader.length >= 3);

/**
 * Helper function to check if two email sends have identical content
 * @param emailA - First email send to compare
 * @param emailB - Second email send to compare
 * @returns boolean indicating if both emails have identical content
 */
export const areEmailContentsIdentical = (emailA: any, emailB: any) =>
    emailA.templateId === emailB.templateId &&
    emailA.senderProfileId === emailB.senderProfileId &&
    emailA.subjectLine === emailB.subjectLine &&
    emailA.preHeader === emailB.preHeader;

// Reserved send names that cannot be used by the user
export const RESERVED_SEND_NAMES = ['Content A', 'Content B', 'Send Time A', 'Send Time B'] as const;

/**
 * Helper function to check if a send name is a reserved name
 * @param name - The name to check
 * @returns boolean indicating if the name is reserved
 */
export const isReservedSendName = (name: string): boolean =>
    RESERVED_SEND_NAMES.includes(name as typeof RESERVED_SEND_NAMES[number]);
